import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import { type AppInventoryState, AppInventoryVersion } from '@openx/types/targeting/appInventory';
import { type InstanceProps, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import type { TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../../../utils/hooks/useTargetingDisabledState';
import { AppInventoryItemsSection } from '../../../InventoryAndContent/InventoryAndContentItems/AppInventoryItemsSection';
import {
  getInventoriesWithSettings,
  isAppBundleIdPayload,
  isAppInventoryPayload,
} from '../../../InventoryAndContent/InventoryAndContentItems/utils';
import type {
  AppInventoryComparisonTypeProps,
  SetInventoryListItemsDispatch,
} from '../../../InventoryAndContent/InventoryAndContentTopBox/constants';
import { resolveAppInventoryOperator } from '../../../shared/AppInventory/utils/resolveAppInventoryOperator';
import type { InvalidItemsError } from '../../../shared/BulkAdd/types';
import { EmptyFilterMessage } from '../../../shared/EmptyFilterMessage/EmptyFilterMessage';
import { useAppInventoryItems } from '../../../shared/InventoryAndContent/hooks/useAppInventoryItems';
import type { TargetingItemsProps } from '../../../types';
import { getInventoryAndContentOptions } from '../../hooks/getInventoryAndContentOptions';

type ContentItemsProps = TargetingItemsProps<AppInventoryState> &
  AppInventoryComparisonTypeProps & {
    inputInstanceValue?: InstanceProps | null;
    appBundleIdDuplicateErrors: InvalidItemsError | null;
    setInventoryListItems?: SetInventoryListItemsDispatch;
    inventoryListItems?: InventoryListByIdAPI['values'];
    field?: TargetingFieldsValues;
  };

export const AppInventoryItems = ({
  targetingParams,
  dispatchInventory = noop,
  readonly,
  accountUid = '',
  appBundleIdDuplicateErrors,
  setInventoryListItems = noop,
  inventoryListItems = [],
  field,
  appNameComparisonType,
  setAppNameComparisonType,
  bundleIdComparisonType,
  setBundleIdComparisonType,
}: ContentItemsProps) => {
  const { t } = useTranslation();
  const { hasError, errorReasonMessage } = useTargetingDisabledState(field);

  const { hasError: hasAppBundleIdError, errorReasonMessage: appBundleIdErrorReasonMessage } =
    useTargetingDisabledState(field ? `${field}.${InventoryContentOption.APP_BUNDLE_ID}` : undefined);

  const { hasError: hasAppGroupError, errorReasonMessage: appGroupErrorReasonMessage } = useTargetingDisabledState(
    field ? `${field}.${InventoryContentOption.APP_GROUP}` : undefined
  );

  const getDisabledStateForOption = (optionKey: string) => {
    if (optionKey === InventoryContentOption.APP_BUNDLE_ID) {
      return {
        isSectionDisabled: hasAppBundleIdError,
        sectionDisabledReasonMessage: appBundleIdErrorReasonMessage,
      };
    }

    if (optionKey === InventoryContentOption.APP_GROUP) {
      return {
        isSectionDisabled: hasAppGroupError,
        sectionDisabledReasonMessage: appGroupErrorReasonMessage,
      };
    }

    return {
      isSectionDisabled: hasError,
      sectionDisabledReasonMessage: errorReasonMessage,
    };
  };

  const inventoriesWithSettings = getInventoriesWithSettings(targetingParams);
  const inventoriesLength = inventoriesWithSettings.length;
  const showSwitch = !(readonly || typeof readonly === 'undefined' || inventoriesWithSettings.length === 0);
  const switchState = resolveAppInventoryOperator(targetingParams) === Criteria.ANY;

  const inventoryAndContent = getInventoryAndContentOptions({
    dispatchInventory,
    readonly,
    targetingParams,
    accountUid,
    appBundleIdDuplicateErrors,
    setInventoryListItems,
    inventoryListItems,
    switchState,
    getDisabledReasonMessage: option => getDisabledStateForOption(option).sectionDisabledReasonMessage,
  });

  const {
    canUseAppInventorySection,
    effectiveAppNameComparisonType,
    effectiveSetAppNameComparisonType,
    effectiveBundleIdComparisonType,
    effectiveSetBundleIdComparisonType,
    filteredInventoriesLength,
    filteredInventories,
  } = useAppInventoryItems({
    showSwitch,
    targetingParams,
    inventoriesWithSettings: inventoriesWithSettings as InventoryContentOption[],
    appNameComparisonType,
    setAppNameComparisonType,
    bundleIdComparisonType,
    setBundleIdComparisonType,
  });

  const shouldRenderAppInventorySection =
    canUseAppInventorySection &&
    isAppInventoryPayload(targetingParams[InventoryContentOption.APP_GROUP]) &&
    isAppBundleIdPayload(targetingParams[InventoryContentOption.APP_BUNDLE_ID]);

  return (
    <>
      {inventoriesLength ? (
        <>
          {/* Render App Inventory with new UI (two sections with toggles) - only when all required props are available */}
          {shouldRenderAppInventorySection && (
            <AppInventoryItemsSection
              appInventoryContent={targetingParams[InventoryContentOption.APP_GROUP]}
              appBundleIdContent={targetingParams[InventoryContentOption.APP_BUNDLE_ID]}
              dispatchInventory={dispatchInventory}
              readonly={!!readonly}
              appNameComparisonType={effectiveAppNameComparisonType}
              setAppNameComparisonType={effectiveSetAppNameComparisonType}
              bundleIdComparisonType={effectiveBundleIdComparisonType}
              setBundleIdComparisonType={effectiveSetBundleIdComparisonType}
              isNotLastItem={filteredInventoriesLength > 0}
              switchState={switchState}
              showDimensionChipLine={true}
              accountUid={accountUid}
              inventoryListItems={inventoryListItems}
              setInventoryListItems={setInventoryListItems}
              inventoryVersion={AppInventoryVersion.APP_INVENTORY_ONLY_V2}
              field={field}
            />
          )}

          {/* Render other inventory types normally */}
          {filteredInventories.map((inventory, index) =>
            inventoryAndContent[inventory](filteredInventoriesLength - 1 > index, true)
          )}
        </>
      ) : (
        <EmptyFilterMessage text={t('No filters applied.')} />
      )}
    </>
  );
};
